import React, { useState, useRef, useEffect } from 'react';
import { Box, styled } from '@mui/material';
import ProtectedImage from './ProtectedImage';
import { ImageProtectionType } from '../../utils/types';

interface MagnifyingGlassProps {
  src: string;
  alt: string;
  zoomFactor?: number; // عامل التكبير (1.5 افتراضيًا)
  magnifierSize?: number; // حجم العدسة بالبكسل
  watermarkText?: string;
  imageProtection?: ImageProtectionType;
  watermarkPosition?: 'top-left' | 'center';
  onClick?: () => void; // إضافة معالج النقر
}

// تنسيق حاوية الصورة
const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&:hover .magnifying-glass': {
    opacity: 1,
  }
}));

// تنسيق عدسة التكبير
const Magnifier = styled(Box)(() => ({
  position: 'absolute',
  pointerEvents: 'none',
  opacity: 0,
  border: '2px solid #fff',
  borderRadius: '50%',
  boxShadow: '0 0 10px rgba(0,0,0,0.3)',
  backgroundRepeat: 'no-repeat',
  transition: 'opacity 0.2s ease',
  zIndex: 10,
}));

const MagnifyingGlass: React.FC<MagnifyingGlassProps> = ({
  src,
  alt,
  zoomFactor = 2,
  magnifierSize = 150,
  watermarkText,
  imageProtection,
  watermarkPosition = 'center',
  onClick,
}) => {
  const [showMagnifier, setShowMagnifier] = useState<boolean>(false);
  const [position, setPosition] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
  const imgRef = useRef<HTMLDivElement | null>(null);

  // حساب موضع العدسة وخلفيتها
  const updateMagnifier = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imgRef.current) return;

    const { left, top, width, height } = imgRef.current.getBoundingClientRect();
    // حساب النسبة بين حجم الصورة الفعلي وحجم العرض
    const { naturalWidth, naturalHeight } = (imgRef.current.querySelector('img') as HTMLImageElement);
    const scaleX = naturalWidth / width;
    const scaleY = naturalHeight / height;

    // حساب موضع العدسة
    const x = e.clientX - left;
    const y = e.clientY - top;

    // تحديد موضع العدسة
    setPosition({ x, y });

    // لا تعرض العدسة إذا كان المؤشر خارج الصورة
    const isInside = x >= 0 && x <= width && y >= 0 && y <= height;
    setShowMagnifier(isInside);
  };

  return (
    <ImageContainer
      ref={imgRef}
      onMouseMove={updateMagnifier}
      onMouseEnter={() => setShowMagnifier(true)}
      onMouseLeave={() => setShowMagnifier(false)}
      onClick={onClick}
      sx={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <ProtectedImage
        src={src}
        alt={alt}
        forceProtection={imageProtection}
        watermarkText={watermarkText}
        watermarkPosition={watermarkPosition}
        sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />

      {showMagnifier && (
        <Magnifier
          className="magnifying-glass"
          sx={{
            backgroundImage: `url(${src})`,
            width: `${magnifierSize}px`,
            height: `${magnifierSize}px`,
            top: `${position.y - magnifierSize / 2}px`,
            left: `${position.x - magnifierSize / 2}px`,
            backgroundSize: `${100 * zoomFactor}% ${100 * zoomFactor}%`,
            backgroundPosition: `
              ${-position.x * zoomFactor + magnifierSize / 2}px 
              ${-position.y * zoomFactor + magnifierSize / 2}px
            `,
          }}
        />
      )}
    </ImageContainer>
  );
};

export default MagnifyingGlass; 